import React, { useCallback } from 'react';
import noop from 'lodash/noop';
import { DropdownItem } from '@atlaskit/dropdown-menu';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { TRIGGER_POINT_KEY_ISSUE_NAVIGATOR } from '@atlassian/jira-issue-archival-modal/src/constants.tsx';
import { useArchiveIssueModalActions } from '@atlassian/jira-issue-archival-modal/src/controllers/main.tsx';
import {
	loadDialog,
	openCreateSubTaskIssueDialog,
	permissionHelper,
	notificationHelper,
	fireSuccessAnalytics,
} from '@atlassian/jira-issue-navigator-issue-operations-services/src/services/utils/utils.tsx';
import {
	OPERATION_IDS,
	LINK_IDS,
} from '@atlassian/jira-issue-operations-commons/src/constants.tsx';
import {
	useIssueOperationsDialogContext,
	type UploadContext,
} from '@atlassian/jira-issue-operations-dialog-context-provider/src/index.tsx';
import { CREATE_SUBTASK_NIN_TRIGGER } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/constants.tsx';
import { useTriggerIssueCreateModal } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/main.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useDeleteIssueModalActions } from '../issue-delete/controllers/main.tsx';
import { useDeleteIssue } from '../issue-delete/services/delete-issue.tsx';
import { useIssueOperationFlags } from '../../notification-flag-group/index.tsx';
import messages from './messages.tsx';

export type Props = {
	issueId: number | null;
	issueKey: string;
	workType?: string | null;
	name: string;
	url: string;
	styleClass: string;
	elementToFocusOnClose?: string;
	uploadPermissions: UploadContext | null;
	onFireAnalyticsEvent: (arg1: string) => void;
	onAddFieldClick: (elementToFocusOnClose?: string) => void;
	onAddFlag: (styleClass: string, isSuccessful: boolean) => void; // cleanup with empanada_nin_convert_issue_ops_to_entrypoint
	onIssueRefetch: () => void;
	onIssueDelete: () => void;
	onIssueDeleteFn?: ((issueKey: string) => void) | undefined;
	onRankToTop: (onSuccess: () => void, onFailure: () => void) => void;
	onRankToBottom: (onSuccess: () => void, onFailure: () => void) => void;
	onApprove: (onSuccess: () => void, onFailure: () => void) => void;
	onReject: (onSuccess: () => void, onFailure: () => void) => void;
	onClose?: () => void;
	isIssueArchivalEnabled?: boolean;
};

export const DropdownButtonItem = ({
	name,
	url,
	styleClass,
	onAddFlag: onAddFlagOld,
	onIssueRefetch,
	onIssueDelete,
	onIssueDeleteFn,
	onRankToTop,
	onRankToBottom,
	onApprove,
	onReject,
	onAddFieldClick,
	issueId,
	issueKey,
	workType,
	onClose,
	uploadPermissions,
	onFireAnalyticsEvent,
	elementToFocusOnClose,
	isIssueArchivalEnabled,
}: Props): React.JSX.Element => {
	const onIssueDeleteNew = useCallback(() => {
		expVal('child-issue-archival-ga', 'isEnabled', false)
			? onIssueDeleteFn?.(issueKey)
			: onIssueDelete();
	}, [issueKey, onIssueDelete, onIssueDeleteFn]);
	const { formatMessage } = useIntl();
	const [, { setDialogContext }] = useIssueOperationsDialogContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsEvent = createAnalyticsEvent({});
	const accountId = useAccountId();
	const deleteIssue = useDeleteIssue();

	const onAddFlagNew = useIssueOperationFlags(issueKey);
	const onAddFlag = fg('empanada_nin_convert_issue_ops_to_entrypoint')
		? onAddFlagNew
		: onAddFlagOld;

	const onSuccess = useCallback(() => {
		fireSuccessAnalytics(analyticsEvent, styleClass);
		styleClass === OPERATION_IDS.DELETE ? onIssueDeleteNew() : onIssueRefetch();
		onAddFlag(styleClass, true);
	}, [analyticsEvent, onAddFlag, onIssueDeleteNew, onIssueRefetch, styleClass]);

	const onFailure = useCallback(() => {
		fireErrorAnalytics({
			meta: {
				id: 'performIssueOperation',
				packageName: 'jiraIssueNavigatorIssueOperations',
				teamName: 'empanada',
			},
			attributes: {
				operation: styleClass,
			},
		});
		onAddFlag(styleClass, false);
	}, [onAddFlag, styleClass]);

	const loadDefaultDialog = () =>
		loadDialog({
			styleClass,
			url,
			onSuccess,
			onFailure,
			issueId,
			accountId,
			elementToFocusOnClose,
		});

	const [, { openIssueCreateModal }] = useTriggerIssueCreateModal();

	const onClickOpenCreateSubTaskIssueDialog = () => {
		if (
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.IssueCreate?.Visibility?.isEnabled &&
			fg('render_modern_global_issue_create_modal_experience')
		) {
			const parentIssueId: string = issueId?.toString() || '';
			openIssueCreateModal({
				isSubTaskCreationOpen: true,
				payload: {
					// this allows to pre-populate the parent field when creating a sub-task from a level 0 issue
					defaultValues: {
						parentField: {
							issueId: parentIssueId,
						},
					},
					parentId: {
						parentId: parentIssueId,
					},
				},
				onIssueCreate: onIssueRefetch,
				triggerPointKey: CREATE_SUBTASK_NIN_TRIGGER,
			});
		} else {
			openCreateSubTaskIssueDialog({
				onDismissAfterCreate: () => {
					onIssueRefetch();
					onAddFlag(OPERATION_IDS.CREATE_SUBTASK, true);
				},
			});
		}
	};

	const { openArchiveIssueModal } = useArchiveIssueModalActions();
	const onIssueArchiveSuccess = () => {
		onIssueDeleteNew();
	};

	const onClickOpenIssueArchiveModal = () => {
		const issueIdArg = issueId?.toString();
		openArchiveIssueModal({
			issueKey,
			issueId: issueIdArg,
			onIssueArchiveSuccess,
			triggerPointKey: TRIGGER_POINT_KEY_ISSUE_NAVIGATOR,
			workType: workType || '',
			onDeleteIssuesFromNINView: expVal('child-issue-archival-ga', 'isEnabled', false)
				? onIssueDeleteFn
				: noop,
		});
	};

	const { openDeleteIssueModal, closeDeleteIssueModal, startDeleteIssue, endDeleteIssue } =
		useDeleteIssueModalActions();

	const onTriggerDeleteRequest = () => {
		try {
			startDeleteIssue();
			deleteIssue({ issueKey });
			endDeleteIssue({ didIssueDeleteFail: false });
			onSuccess();
		} catch (error) {
			endDeleteIssue({ didIssueDeleteFail: true });
			onFailure();
		}
		closeDeleteIssueModal();
	};

	const onClickOpenIssueDeleteModal = () => {
		openDeleteIssueModal({
			isIssueArchivalEnabled: isIssueArchivalEnabled || false,
			workType,
			issueKey,
			issueId: issueId?.toString(),
			onTriggerDeleteRequest,
			onIssueDeleteSuccess: onSuccess,
			onIssueDeleteFailure: onFailure,
		});
	};

	let onClick: (() => Promise<void> | React.JSX.Element) | (() => void) = loadDefaultDialog;
	let text = name;

	switch (styleClass) {
		case OPERATION_IDS.DELETE:
			if (fg('issue_deletion_two_step_process_free_standard')) {
				onClick = onClickOpenIssueDeleteModal;
			}
			break;
		case OPERATION_IDS.APPROVE_APPROVAL:
			onClick = () => onApprove(onSuccess, onFailure);
			break;
		case OPERATION_IDS.REJECT_APPROVAL:
			onClick = () => onReject(onSuccess, onFailure);
			break;
		case OPERATION_IDS.RANK_TO_TOP:
			onClick = () => onRankToTop(onSuccess, onFailure);
			break;
		case OPERATION_IDS.RANK_TO_BOTTOM:
			onClick = () => onRankToBottom(onSuccess, onFailure);
			break;
		case OPERATION_IDS.ADD_FIELD:
			onClick = () => onAddFieldClick(elementToFocusOnClose);
			break;
		case OPERATION_IDS.CREATE_SUBTASK:
			onClick = onClickOpenCreateSubTaskIssueDialog;
			break;
		case OPERATION_IDS.PERMISSION_HELPER:
			onClick = () => permissionHelper(elementToFocusOnClose);
			break;
		case OPERATION_IDS.NOTIFICATION_HELPER:
			onClick = notificationHelper;
			break;
		case LINK_IDS.LINK_ISSUE:
			text = formatMessage(
				fg('jira-issue-terminology-update-m4')
					? messages.linkIssueIssueTerminologyRefreshM4
					: messages.linkIssue,
			);
			break;
		case LINK_IDS.LINK_CONFLUENCE_PAGE:
			text = formatMessage(messages.linkConfluencePage);
			break;
		case LINK_IDS.ADD_WEB_LINK:
			text = formatMessage(messages.addWebLink);
			break;
		case OPERATION_IDS.ATTACH_FILE: // Remove with cleanup of empanada_nin_convert_issue_ops_to_entrypoint
			onClick = () =>
				setDialogContext(OPERATION_IDS.ATTACH_FILE, issueId, issueKey, uploadPermissions, workType);
			break;
		case OPERATION_IDS.ARCHIVE:
			onClick = () => onClickOpenIssueArchiveModal();
			break;
		default:
			break;
	}

	return (
		<DropdownItem
			key={styleClass}
			onClick={() => {
				onClick();
				onFireAnalyticsEvent(styleClass);
				onClose?.();
			}}
			data-issueid={issueId}
			data-issuekey={issueKey}
			data-url={url}
			testId="issue-navigator-issue-operations.ui.dropdown-menu-group.button-item.button-item"
		>
			{text}
		</DropdownItem>
	);
};

export default DropdownButtonItem;
